<script setup lang="ts">
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';

import { BackgroundProps, BackgroundType } from '@/shared/components/base/Background/types.ts';
import SafeArea from '@/shared/components/mobile/SafeArea/SafeArea.vue';
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue';
import { injectMediaLoader } from '@/shared/composables/useMediaLoader.ts';
import { MaybeNull } from '@/shared/types';
import { getSafeRef } from '@/shared/utils/template-refs.ts';
import { createAnimation, Animation } from '@ionic/vue';

const props = withDefaults(defineProps<BackgroundProps>(), {
  paused: false,
  /**
   * If true, the media file will not be removed from the cache (revokeObjectURL) when the component is unmounted.
   * This is useful for keeping video in memory when navigating between reading plan days.
   */
  persistent: false,
  background: () => ({
    type: BackgroundType.Empty,
  }),
});

const emit = defineEmits<{
  (e: 'loaded-media'): void;
  (e: 'video-loadeddata'): void;
}>();

const bgImage$ = ref<MaybeNull<HTMLElement>>(null);
const bgVideo$ = ref<MaybeNull<HTMLVideoElement>>(null);

let backgroundAnimation: MaybeNull<Animation> = null;

const getBackgroundAnimationByBackgroundType = (type: BackgroundType): Animation => {
  switch (type) {
    case BackgroundType.Video: {
      return createAnimation()
        .addElement(getSafeRef(bgVideo$))
        .duration(3000)
        .easing('cubic-bezier(0.22, 1, 0.36, 1)')
        .fromTo('transform', 'scale(1.2) translate(-42%, 0%)', 'scale(1) translate(-50%, 0%)');
    }
    case BackgroundType.Image: {
      return createAnimation()
        .addElement(getSafeRef(bgImage$))
        .duration(3000)
        .easing('cubic-bezier(0.22, 1, 0.36, 1)')
        .fromTo('transform', 'scale(1.2)', 'scale(1)');
    }
    default: throw Error(`Enable to get background animation options. There is no handler for ${type} background type`);
  }
};

const animate = () => {
  if (backgroundAnimation) {
    backgroundAnimation.play();
  }
};

const reset = () => {
  if (backgroundAnimation) {
    backgroundAnimation.stop();
  }
};

let resetAnimationTimeout: MaybeNull<number> = null;

watch(() => props.active, (value) => {
  if (value) {
    if (!resetAnimationTimeout) {
      animate();
    } else {
      clearTimeout(resetAnimationTimeout);
    }
  } else {
    resetAnimationTimeout = window.setTimeout(() => {
      reset();
      resetAnimationTimeout = null;
    }, 500);
  }
});

const onPausedChange = async (value: boolean) => {
  try {
    if (!value) {
      await getSafeRef(bgVideo$).play();
    } else {
      getSafeRef(bgVideo$).pause();
    }
  } catch (error) {
    console.error('Error while playing/pausing background video:', error);
  }
};

watch(() => props.paused, onPausedChange);

watch(() => props.frozen, onPausedChange);

const mediaUrl = ref<string>('');

const mediaLoader = injectMediaLoader();

let loadId = '';

const loadMediaByBackgroundType = (backgroundType: BackgroundType) => {
  switch (backgroundType) {
    case BackgroundType.Video: {
      // For video, we use direct URL to allow streaming (range requests)
      // Browser will handle caching based on headers
      emit('loaded-media');
    }
      break;
    case BackgroundType.Image: {
      // @ts-expect-error image is not typed
      loadId = mediaLoader.load(props.background.image, (url: string) => {
        mediaUrl.value = url;

        nextTick(() => {
          emit('loaded-media');
        });

        createAnimation()
          .addElement(getSafeRef(bgImage$))
          .duration(500)
          .easing('ease')
          .fromTo('opacity', '0', '1')
          .fill('forwards')
          .play();
      });
    }
      break;
    default:
  }
};

onMounted(() => {
  if (props.background.animate) {
    try {
      backgroundAnimation = getBackgroundAnimationByBackgroundType(props.background.type);
    } catch (e) {
      console.error(e);
    }
  }

  loadMediaByBackgroundType(props.background.type);
});

watch(() => props.background, (newVal, oldVal) => {
  // Only reset and reload if the actual media source or type has changed
  const getUrl = (bg: typeof props.background) => {
    if (bg.type === BackgroundType.Video) {
      return bg.video.url;
    }
    if (bg.type === BackgroundType.Image) {
      return bg.image.url;
    }

    return '';
  };

  if (newVal.type === oldVal?.type && getUrl(newVal) === getUrl(oldVal)) {
    return;
  }

  // clear prev media url to reset video player while loading new media
  mediaUrl.value = '';

  mediaLoader.cancelLoad(loadId);

  loadMediaByBackgroundType(props.background.type);
});

onBeforeUnmount(() => {
  mediaLoader.cancelLoad(loadId);
  if (!props.persistent) {
    mediaLoader.revoke(mediaUrl.value);
  }
});

watch(() => mediaUrl.value, (newValue, oldValue) => {
  mediaLoader.revoke(oldValue);
});

const container$ = ref<MaybeNull<HTMLElement>>(null);

defineExpose({
  container$,
});

const videoPoster = computed(() => {
  return (props.background.type === BackgroundType.Video && props.background.video.cover?.url) || '';
});

</script>

<template>
  <div
    ref="container$"
    class="background-container"
  >
    <div class="background-content">
      <slot />
    </div>
    <div
      v-if="background.mask"
      class="mask"
      :style="{
        background: background.mask.color,
        opacity: background.mask.opacity / 100,
      }"
    />
    <img
      v-if="props.background.type === BackgroundType.Image"
      ref="bgImage$"
      data-media-marker="main-component-media"
      class="bg-image"
      :src="mediaUrl"
    >
    <video
      v-if="props.background.type === BackgroundType.Video"
      id="bg-video-player"
      ref="bgVideo$"
      data-media-marker="main-component-media"
      autoplay
      loop
      muted
      playsinline
      height="100%"
      class="bg-video"
      :src="props.background.video.url"
      :poster="videoPoster"
      @loadeddata="emit('video-loadeddata')"
    />

    <FadeTransition :visible="paused">
      <SafeArea
        class="icon-play-wrapper"
        fixed
      >
        <Icon
          name="play"
          class="icon-play"
        />
      </SafeArea>
    </FadeTransition>
  </div>
</template>

<style scoped>
.background-container {
  width: 100%;
  height: 100%;
  padding: 0;
  position: relative;
  overflow: hidden;
}

.background-content {
  z-index: 1;
  height: 100%;
  position: relative;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  object-fit: cover;
  object-position: 50% 50%;
  transform: scale(1.2);
  opacity: 0;
}

.bg-video {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  z-index: -1;
  transform: scale(1.2) translate(-42%, 0%);
  margin-left: 50%;
  object-fit: cover;
}

video.bg-video {
  max-width: unset !important;
  max-height: 100%;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.icon-play-wrapper {
  pointer-events: none;

  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
}

.icon-play {
  color: rgba(255, 255, 255, 0.4);
  font-size: 50px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
